/**
 * Name folding for guest lookups in Studio.
 *
 * This file used to also hold `planInviteeImport` — a TypeScript model of the
 * API's matching rule, which rewrote each outgoing row to *borrow* a match key
 * off the roster entry it thought the row meant, so the API would land on it.
 * That is gone, deliberately: a model of a rule is a second rule, and these two
 * disagreed in ways the host could not see. The plan said "updates Aaron Diaz"
 * and the write updated Zoe Park (Studio scanned the roster in display order and
 * took the first entry matching on any key; the SQL ranked email above phone).
 * The plan said "0 duplicates" and the write replaced one guest's name with
 * another's (Studio tracked which guests an earlier row had claimed; the SQL had
 * no memory between rows). And borrowing a key rewrote `source`/
 * `sourceExternalId`, destroying a guest's provenance on a successful update.
 *
 * Matching now happens once, on the server, in `InviteeImportMatcher`. Studio
 * asks it what would happen (`previewInviteeImport`), shows the host each
 * proposed match, and posts back only the invitee ids the host accepted.
 *
 * What remains here is the fold, still needed locally for searching and
 * de-duplicating guest labels in the seating and occupant tools.
 */

/**
 * Fold a name to a comparison key. Deliberately mirrors the API's
 * `GroupTrackerRsvp.NormalizeImportText`: names pasted out of Partiful and Apple
 * devices carry non-breaking spaces and curly quotes, and if the two sides fold
 * differently a search misses a guest who is plainly on the roster.
 */
export function normalizeName(name?: string | null): string {
  return (name || '')
    // Escapes, not literals: these fold characters are invisible or near-invisible
    // in an editor, and a lost one silently stops folding.
    .replace(/[\u00a0\u2007\u202f\ufeff]/g, ' ')
    .replace(/[\u2018\u2019\u201b]/g, "'")
    .replace(/[\u201c\u201d]/g, '"')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}
